import { type Player, Role, type Room } from '@infiltre/shared';
import { motion } from 'motion/react';
import { type MessageKey, useT } from '@/shared/lib/i18n';
import { mugshotForPlayer } from '@/shared/lib/mugshot';
import { roleTint } from '@/shared/lib/role';
import { Button, MugshotPortrait, PaperSheet, PhaseHeader, Pin } from '@/shared/ui';

export type VerdictScreenProps = {
  room: Room;
  eliminated: Player;
  onContinue: () => void;
  continueLabel: string;
};

const fileTagFromId = (id: string): string => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return `F-${String(h % 1000).padStart(3, '0')}`;
};

/**
 * Round verdict — pinned mugshot polaroid stamped with a magenta cross
 * and "ÉLIMINÉ" tape, role-tinted reveal slab below. Sibling to
 * `FinishedScreen`; both live under `round-result/`.
 */
export const VerdictScreen = ({ eliminated, onContinue, continueLabel }: VerdictScreenProps) => {
  const t = useT();
  const role = eliminated.role;
  const tint = roleTint(role);
  const tagline = (() => {
    if (role === Role.Flic) return t('round.verdict.role_tagline.flic');
    if (role === Role.Infiltre) return t('round.verdict.role_tagline.infiltre');
    if (role === Role.Clochard) return t('round.verdict.role_tagline.clochard');
    return '';
  })();
  const roleLabel = role ? t(`role.${role}` as MessageKey) : '';
  const order = eliminated.order ?? 0;

  return (
    <main className="mx-auto flex h-full w-full max-w-md flex-col">
      <PhaseHeader
        eyebrow={t('round.verdict.phase_eyebrow')}
        title={t('round.verdict.stamp')}
        descriptor={t('round.verdict.descriptor')}
      />

      <div className="flex flex-1 flex-col items-center justify-center gap-6 px-5 py-6">
        <motion.div
          initial={{ opacity: 0, y: 10, rotate: 0, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, rotate: -2.5, scale: 1 }}
          transition={{ duration: 0.4, ease: [0.18, 1.15, 0.4, 1] }}
          className="relative w-[260px]"
        >
          <Pin left="50%" top={-8} size={14} />
          <PaperSheet
            tone="cream"
            seed={eliminated.id}
            className="px-3 pt-3 pb-3"
            style={{ boxShadow: 'var(--shadow-paper-deep)' }}
          >
            <div className="relative aspect-[4/5] w-full overflow-hidden border border-ink/15">
              <MugshotPortrait config={mugshotForPlayer(eliminated)} />
              <span className="absolute top-1.5 left-2 font-mono text-[11px] text-ink/70 tracking-[0.2em]">
                #{String(order).padStart(3, '0')}
              </span>
              <span className="absolute right-2 bottom-1.5 font-mono text-[11px] text-ink/55 tracking-[0.18em]">
                {fileTagFromId(eliminated.id)}
              </span>
              <motion.svg
                aria-hidden
                initial={{ opacity: 0, scale: 0.6, rotate: -10 }}
                animate={{ opacity: 0.92, scale: 1, rotate: 0 }}
                transition={{ duration: 0.4, delay: 0.18, ease: [0.18, 1.4, 0.4, 1] }}
                className="absolute inset-0 m-auto"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                style={{ width: '95%', height: '95%' }}
              >
                <line
                  x1="8"
                  y1="8"
                  x2="92"
                  y2="92"
                  stroke="var(--color-magenta-hi)"
                  strokeWidth="14"
                  strokeLinecap="round"
                />
                <line
                  x1="92"
                  y1="8"
                  x2="8"
                  y2="92"
                  stroke="var(--color-magenta-hi)"
                  strokeWidth="14"
                  strokeLinecap="round"
                />
              </motion.svg>
              <motion.div
                initial={{ rotate: -22, scale: 1.4, opacity: 0 }}
                animate={{ rotate: -7, scale: 1, opacity: 1 }}
                transition={{ duration: 0.42, delay: 0.32, ease: [0.18, 1.4, 0.4, 1] }}
                className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 px-3 py-1 font-display font-extrabold text-[14px] uppercase leading-none tracking-[0.18em]"
                style={{
                  background: 'rgba(232, 62, 132, 0.55)',
                  color: 'var(--color-stamp-red)',
                  border: '2px solid var(--color-stamp-red)',
                  textShadow: '0 1px 0 rgba(255,255,255,.25)',
                }}
              >
                {t('round.verdict.stamp')}
              </motion.div>
            </div>
            <div className="mt-2 truncate text-center font-hand text-[26px] text-ink leading-tight">
              {eliminated.name}
            </div>
          </PaperSheet>
        </motion.div>

        {role && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.32, delay: 0.55 }}
            className="w-full max-w-[320px] rounded-md border-2 px-6 py-4 text-center"
            style={{ borderColor: tint, background: 'rgba(0,0,0,.35)' }}
          >
            <div
              className="eyebrow text-[10px] tracking-[0.22em]"
              style={{ color: tint, opacity: 0.55 }}
            >
              {t('round.verdict.role_revealed')}
            </div>
            <div
              className="mt-1 font-display font-extrabold text-[34px] italic uppercase leading-none"
              style={{ color: tint }}
            >
              {roleLabel}
            </div>
            {tagline && (
              <div className="mt-1.5 text-[12px] italic" style={{ color: tint, opacity: 0.6 }}>
                {tagline}
              </div>
            )}
          </motion.div>
        )}
      </div>

      <div className="px-5 pt-2 pb-5">
        <Button variant="orange" onClick={onContinue}>
          {continueLabel}
        </Button>
      </div>
    </main>
  );
};
